import {
  Button,
  keyframes,
  PlacementWithLogical,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { SparklesIcon } from '@/components/common/Icons/SparklesIcon';
import { useWizardContext } from '@/components/wizard/WizardContext';

interface WizardAskButtonProps {
  question: string;
  placement?: PlacementWithLogical | 'auto';
  testid?: string;
  children: ReactNode;
}

const gradientShift = keyframes`
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
`;

export const WizardAskButton = ({ question, testid, children, placement }: WizardAskButtonProps) => {
  const [showButton, setShowButton] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setIsWizardOpen, setContextMessages } = useWizardContext();

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleButtonClick = () => {
    setIsWizardOpen(() => true);
    setContextMessages([
      {
        id: '1',
        content: `請問什麼是 ${question}`,
        role: 'user',
      },
    ]);
    onClose();
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    onOpen();
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
      closeTimeoutRef.current = null;
    }, 1000);
  };

  useEffect(() => {
    // Check if the environment variable is set to show the button
    // You can define the environment variable as REACT_APP_SHOW_WIZARD_BUTTON, NEXT_PUBLIC_SHOW_WIZARD_BUTTON, etc.
    // depending on your framework (Create React App, Next.js, etc.)
    const envVar = process.env.NEXT_PUBLIC_SHOW_WIZARD_BUTTON;
    setShowButton(envVar === 'true' || envVar === '1');
  }, []);

  // If the button should be hidden based on environment variable, return just the children
  if (!showButton) {
    return <>{children}</>;
  }

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement={placement} closeOnBlur={true}>
      <PopoverTrigger>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {children}
        </div>
      </PopoverTrigger>
      {isOpen && (
        <PopoverContent
          bg='transparent'
          border='none'
          boxShadow='none'
          width='auto'
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          p={2}
          style={{
            transform: 'translateY(-5px)',
          }}
        >
          <PopoverBody
            padding={0}
            style={{
              padding: '16px',
              margin: '-16px',
            }}
          >
            <Button
              bgGradient='linear(to-r, rgba(255, 56, 101, 0.3), rgba(253, 91, 177, 0.3), rgba(219, 153, 255, 0.3), rgba(45, 189, 255, 0.3), rgba(252, 173, 40, 0.3))'
              color='black'
              leftIcon={<SparklesIcon />}
              data-testid={testid ? `wizard-ask-button-${testid}` : undefined}
              onClick={handleButtonClick}
              size='md'
              shadow={'md'}
              borderRadius='lg'
              position='relative'
              backgroundSize='200% 200%'
              transition='all 0.3s ease'
              fontWeight='normal'
              animation={`${gradientShift} 3s ease infinite`}
              _before={{
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'lg',
                padding: '1px',
                background: 'linear-gradient(to right, #FF3865, #FD5BB1, #DB99FF, #2DBDFF, #FCAD28)',
                backgroundSize: '200% 200%',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                opacity: 0.8,
                transition: 'all 0.3s ease',
                animation: `${gradientShift} 3s ease infinite`,
              }}
              boxShadow='xl'
              _focus={{
                boxShadow: 'none',
              }}
              _active={{
                boxShadow: 'none',
              }}
              _hover={{
                bgGradient:
                  'linear(to-r, rgba(255, 56, 101, 0.3), rgba(253, 91, 177, 0.3), rgba(219, 153, 255, 0.3), rgba(45, 189, 255, 0.3), rgba(252, 173, 40, 0.3))',
              }}
              sx={{
                '&::before': {
                  animation: `${gradientShift} 3s ease infinite`,
                },
              }}
            >
              Explain
            </Button>
          </PopoverBody>
        </PopoverContent>
      )}
    </Popover>
  );
};
